import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'

type ViewSwitchOption<T extends string> = {
  value: T
  label: string
  leadingIcon?: IconTypes
}

type ViewSwitchProps<T extends string> = {
  options: ViewSwitchOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
}

export function ViewSwitch<T extends string>({ options, value, onChange, ariaLabel }: ViewSwitchProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="tw:inline-flex tw:w-fit tw:shrink-0 tw:overflow-hidden tw:rounded-(--embed-radius-sm,0.5rem) tw:border tw:border-gray-300 tw:bg-white"
    >
      {options.map((option, index) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'tw:flex tw:items-center tw:gap-1.5 tw:whitespace-nowrap tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              'tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:-outline-offset-2 tw:focus-visible:outline-(--embed-brand-ink,#111827)',
              index > 0 && 'tw:border-l tw:border-gray-300',
              selected
                ? 'tw:bg-(--embed-brand-soft,#f5f3ff) tw:text-(--embed-brand-ink,#6d28d9)'
                : 'tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {option.leadingIcon && <Icon type={option.leadingIcon} className="tw:h-4 tw:w-4" aria-hidden />}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
